import { supabase } from './supabase';

// Races a promise against a timeout so an awaited call ALWAYS settles. Without
// this, a hung Supabase request never resolves, the handler's finally never
// runs, and a loading flag can strand true forever. (Guy Off, daily start, etc.)
export function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

// On tab resume, supabase-js can stall for several seconds finishing a token
// refresh before it dispatches ANY data request. A plain withTimeout then rejects
// even though the connection is healthy and the fetch would return 200 (confirmed
// via the Network panel: requests dispatch + 200 right after the app already gave
// up). This runs the call; if it times out, it waits for the auth session to
// settle (the refresh to finish) and retries once with a ready token. Bounded
// throughout, so a genuinely dead connection still surfaces as an error.
export async function fetchWithAuthRetry(fn, { ms = 2500, settleMs = 3000, retryMs = 3000 } = {}) {
  try {
    return await withTimeout(fn(), ms);
  } catch (e) {
    if (e?.message !== 'timeout') throw e;
    try { await withTimeout(supabase.auth.getSession(), settleMs); } catch {}
    return await withTimeout(fn(), retryMs);
  }
}

// Runs a fetch fn up to `tries` times, each attempt bounded by `withTimeout`.
// Used for required initial loads (rosters) so a hang becomes a fast retry
// instead of an infinite spinner.
export async function loadWithRetry(fn, { ms = 8000, tries = 3 } = {}) {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try { return await withTimeout(fn(), ms); }
    catch (e) { lastErr = e; }
  }
  throw lastErr;
}
